/** The generated asset manifest (tools/build-assets.mjs → public/generated/manifest.json). */

export interface FighterRig {
  name: string;
  color: string;
  body: { drawHeight: number; pixelSize: [number, number] };
  fist: { drawWidth: number; pixelSize: [number, number] };
  fists: {
    rest: { lead: [number, number]; rear: [number, number] };
    block: { lead: [number, number]; rear: [number, number] };
  };
  nameTagHeight: number;
}

export type BodyState = 'idle' | 'sting' | 'rage' | 'ko' | 'win';

export interface FighterManifest {
  id: string;
  name: string;
  color: string;
  /** PixiJS spritesheet JSON with frames body_<state>, flash_<state> and fist. */
  sheet: string;
  /** Standalone images for the menus. */
  images: Record<BodyState, string>;
  fistImage: string;
  /** Height in atlas pixels of the full (untrimmed) body image. */
  bodyPixelHeight: number;
  frameHeightRatio: number;
  fistPixelWidth: number;
  rig: FighterRig;
}

export interface ArenaManifest {
  id: string;
  image: string;
  imageSmall: string;
  smallScale: number;
  pixelSize: [number, number];
  center: [number, number];
  apothems: number[];
}

/** A Locker glove design (assets/gloves): worn in place of any fighter's own fists. */
export interface GloveManifest {
  id: string;
  /** Menu image (WebP). */
  image: string;
  /** Width in atlas pixels; the frame in the gloves atlas is named after the id. */
  pixelWidth: number;
  /** Width in multiples of S, as a fighter's `rig.fist.drawWidth`. */
  drawWidth: number;
}

/**
 * A Locker pet (assets/pets/pets.json): a body and, for most, a wing that flaps (the left wing is
 * its mirror) or a rotor that spins. Lengths are in body heights from the body's bottom centre
 * (x to the right, y negative upward), as in pets.json.
 */
export interface PetManifest {
  id: string;
  /** Menu images (WebP); in the fight atlas the frames are <id>_body and <id>_wing. */
  body: string;
  wingImage: string | null;
  /** The pet with its wings spread, for the Locker's cards. */
  icon: string;
  /** Body height in multiples of S. */
  height: number;
  /** Body height in atlas pixels. */
  bodyPixelHeight: number;
  /** Body width / height. */
  bodyAspect: number;
  wing: {
    style: 'flap' | 'spin';
    /** Wing image width, and its height / width. */
    width: number;
    aspect: number;
    /** The point it turns about, as fractions of the wing image, and where that sits on the body. */
    root: [number, number];
    at: [number, number];
    /** Flaps (or turns) per second, and the flap's swing in radians. */
    hz: number;
    swing: number;
  } | null;
  /** sway: no wings; the body sways as it floats. */
  float: 'sway' | null;
  /** A soft light around the pet (diameter `size` in body heights, centre `at`). */
  glow: { color: string; at: [number, number]; size: number } | null;
}

export interface AssetManifest {
  fighters: FighterManifest[];
  arenas: Record<string, ArenaManifest>;
  /** The Locker's gloves: one spritesheet for the fight (null without any). */
  gloves?: { sheet: string | null; items: GloveManifest[] };
  /** The Locker's pets: one spritesheet for the fight (null without any). */
  pets?: { sheet: string | null; items: PetManifest[] };
}

declare const __BUILD_TIME__: string;

let cached: Promise<AssetManifest> | null = null;

export function assetUrl(path: string): string {
  return `${import.meta.env.BASE_URL}${path}`;
}

export function loadManifest(): Promise<AssetManifest> {
  // This build's manifest: generated/ files are cached for a day (public/_headers, service
  // worker), and an older manifest wouldn't know fighters added since.
  cached ??= fetch(assetUrl(`generated/manifest.json?b=${encodeURIComponent(__BUILD_TIME__)}`)).then((r) => {
    if (!r.ok) throw new Error(`manifest ${r.status}`);
    return r.json() as Promise<AssetManifest>;
  });
  return cached;
}

/** A fighter this build doesn't know (one added since) is drawn as the first one, not a crash. */
export function fighterById(m: AssetManifest, id: string): FighterManifest {
  const f = m.fighters.find((x) => x.id === id) ?? m.fighters[0];
  if (!f) throw new Error('No fighters in the asset manifest');
  return f;
}
